import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import { Textarea } from '@/components/Forms';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';

import AttachedCountBadge from '../AttachedCountBadge';
import MoreImagesModal from '../MoreImagesModal';
import UploadMultipleImages from '../UploadMultipleImages';
import {
  AddMoreButton,
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  LabelText,
  MoreImages,
  ResetButton,
  UploadedImages,
} from '../VariationOne';
import { ImageGrid, ImagesBox, TextAreaBox } from '../VariationTwo';
import { getWatermarkPosition, getWatermarkTag } from '../shared';
import Filler from '../shared/Filler';
import { ActionBox, ActionTitle } from '../shared/styledComponents';
import GeneratedImages from './GeneratedImages';
import UploadedImageRow from './UploadedImageRow';
import VariationsStyles from './VariationStyles';

export interface ImageVariation {
  leftImageSrc: string;
  rightImageSrc: string;
  rightText: string;
  brakes: boolean;
  quotes: boolean;
  divider: string;
  leftWatermarkTag: string;
  leftWatermarkPosition: string;
  rightWatermarkTag: string;
  rightWatermarkPosition: string;
  textPosition: string;
  fullText: boolean;
}

const StyledTextAreaBox = styled(TextAreaBox)`
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

const StyledTextArea = styled(Textarea)`
  height: 90px;
`;

const StyledUploadedImages = styled(UploadedImages)`
  height: 360px;
`;

const StyledUploadMultipleImages = styled(UploadMultipleImages)`
  height: 209px;
`;

interface Props {
  inArticle?: boolean;
}

const VariationImgImgText: FC<Props> = ({ inArticle }) => {
  const [showContent, setShowContent] = useState(true);
  const [text, setText] = useState('');
  const [fullText, setFullText] = useState(false);
  const [brakes, setBrakes] = useState(false);
  const [quotes, setQuotes] = useState(false);

  const [leftImages, setLeftImages] = useState<ImageData[]>([]);
  const [rightImages, setRightImages] = useState<ImageData[]>([]);

  const [showLeftImagesModal, setShowLeftImagesModal] = useState(false);
  const [showRightImagesModal, setShowRightImagesModal] = useState(false);

  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [images, setImages] = useState<string[]>();

  const onReset = () => {
    setLeftImages([]);
    setRightImages([]);
    setVariations([]);
    setImages([]);
    setText('');
    setBrakes(false);
    setFullText(false);
    setQuotes(false);
  };

  const generateVariations = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const newVariations = leftImages.flatMap((leftImage) =>
      rightImages.map((rightImage) => ({
        leftImageSrc:
          leftImage.croppedImage ?? URL.createObjectURL(leftImage.image),
        rightImageSrc:
          rightImage.croppedImage ?? URL.createObjectURL(rightImage.image),
        leftWatermarkTag: getWatermarkTag(leftImage),
        leftWatermarkPosition: getWatermarkPosition(leftImage),
        rightWatermarkTag: getWatermarkTag(rightImage),
        rightWatermarkPosition: getWatermarkPosition(rightImage),
        rightText: text,
        divider: leftImage.divider,
        brakes: brakes,
        quotes: quotes,
        textPosition: leftImage.textPosition,
        fullText: fullText,
      })),
    );

    setVariations(newVariations);
  };

  const renderLeftImages = leftImages.map((image, index) => (
    <UploadedImageRow
      type="left"
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setLeftImages}
    />
  ));

  const renderRightImages = rightImages.map((image, index) => (
    <UploadedImageRow
      type="right"
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setRightImages}
    />
  ));

  const isResetDisabled =
    !leftImages?.length && !text && !brakes && !rightImages?.length;

  return (
    <>
      <VariationsStyles />
      <Box>
        <Header>
          <div>
            <Heading $type="button">Img + Img + Text</Heading>
            <Text $type="smallHeadline" $color="gray">
              Attach two half images and text with the necessary parameters
            </Text>
          </div>
          <HeaderButtons>
            <ResetButton
              color="red"
              disabled={isResetDisabled}
              onClick={onReset}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <>
            <ImageGrid>
              <ImagesBox $hasImages={!!leftImages?.length}>
                <LabelText $type="smallHeadline">Left Images</LabelText>
                {!!leftImages?.length ? (
                  <>
                    <StyledUploadedImages>
                      {renderLeftImages}
                    </StyledUploadedImages>
                    <MoreImages>
                      <AttachedCountBadge count={leftImages.length} />
                      <AddMoreButton
                        onClick={() => setShowLeftImagesModal(true)}
                        color="colorized"
                      >
                        Add more images
                      </AddMoreButton>
                    </MoreImages>
                  </>
                ) : (
                  <StyledUploadMultipleImages
                    coverIndex={2}
                    textPosition="top"
                    setImages={setLeftImages}
                  />
                )}
              </ImagesBox>
              <ImagesBox $hasImages={!!rightImages?.length} $hasLeftBorder>
                <LabelText $type="smallHeadline">Right Images</LabelText>
                {!!rightImages?.length ? (
                  <>
                    <StyledUploadedImages>
                      {renderRightImages}
                    </StyledUploadedImages>
                    <MoreImages>
                      <AttachedCountBadge count={rightImages.length} />
                      <AddMoreButton
                        onClick={() => setShowRightImagesModal(true)}
                        color="colorized"
                      >
                        Add more images
                      </AddMoreButton>
                    </MoreImages>
                  </>
                ) : (
                  <StyledUploadMultipleImages
                    coverIndex={2}
                    noneDivider
                    setImages={setRightImages}
                  />
                )}
              </ImagesBox>
            </ImageGrid>
            <StyledTextAreaBox>
              <div>
                <LabelText $type="smallHeadline">Full Text</LabelText>
                <StyledTextArea
                  onChange={(e) => setText(e.target.value)}
                  value={text}
                  placeholder="Add text"
                ></StyledTextArea>
              </div>
              <div>
                <ActionTitle $type="smallHeadline">Text Parameters</ActionTitle>
                <ActionBox>
                  <Checkbox
                    checked={brakes}
                    onChange={() => setBrakes((prev) => !prev)}
                  >
                    Custom brakes
                  </Checkbox>
                  <Checkbox
                    checked={quotes}
                    onChange={() => setQuotes((prev) => !prev)}
                  >
                    Quotes
                  </Checkbox>
                  <Checkbox
                    checked={fullText}
                    onChange={() => setFullText((prev) => !prev)}
                  >
                    Full Text
                  </Checkbox>
                </ActionBox>
              </div>
            </StyledTextAreaBox>
          </>
        )}
      </Box>
      <Filler />
      {showContent && (
        <ButtonHolder>
          <Button
            disabled={!leftImages?.length || !rightImages?.length}
            onClick={generateVariations}
          >
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />
      <AnimatePresence>
        {!!showLeftImagesModal && (
          <MoreImagesModal
            onClose={() => setShowLeftImagesModal(false)}
            setImages={setLeftImages}
            coverIndex={1}
          />
        )}
        {!!showRightImagesModal && (
          <MoreImagesModal
            onClose={() => setShowRightImagesModal(false)}
            setImages={setRightImages}
            coverIndex={2}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VariationImgImgText;
